import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { Portal } from 'react-portal';
import classnames from 'classnames';

import { ROUTES } from 'shared/constants';
import { useObservable } from 'shared/hooks';
import { TaskViewModel } from 'shared/models/TaskTracker';
import { UiAvatar, UiIcon, UiNavLink } from 'shared/uikit-new';
import { toDate } from 'shared/utilities';

import './index.scss';

const OFFSET = 8;
const VIEWPORT_PADDING = 16;
const MAX_HEIGHT = 320;
const MIN_HEIGHT = 120;
const CLOSE_DELAY = 150;
const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';

type PropsType = {
    taskViews?: TaskViewModel[];
    className?: string;
};

export const CTaskViews = observer(function CTaskViews({ taskViews = [], className }: PropsType) {
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const store = useObservable({
        isOpened: false,
        isPositioned: false,
        canHover: window.matchMedia(HOVER_MEDIA_QUERY).matches,
        top: 0,
        left: 0,
        maxHeight: MAX_HEIGHT,
    });

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const dropdown = dropdownRef.current;
        if (!trigger || !dropdown) {
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        const spaceBelow = viewportHeight - triggerRect.bottom - OFFSET - VIEWPORT_PADDING;
        const spaceAbove = triggerRect.top - OFFSET - VIEWPORT_PADDING;
        const contentHeight = Math.min(MAX_HEIGHT, dropdown.scrollHeight);

        const isAbove = spaceBelow < contentHeight && spaceAbove > spaceBelow;
        const space = Math.max(isAbove ? spaceAbove : spaceBelow, 0);
        const maxHeight = Math.min(MAX_HEIGHT, Math.max(space, MIN_HEIGHT));
        const height = Math.min(contentHeight, maxHeight);
        const width = dropdown.offsetWidth;

        const maxTop = Math.max(viewportHeight - VIEWPORT_PADDING - height, VIEWPORT_PADDING);
        const top = Math.min(
            Math.max(isAbove ? triggerRect.top - OFFSET - height : triggerRect.bottom + OFFSET, VIEWPORT_PADDING),
            maxTop
        );

        const maxLeft = Math.max(viewportWidth - VIEWPORT_PADDING - width, VIEWPORT_PADDING);
        const left = Math.min(Math.max(triggerRect.right - width, VIEWPORT_PADDING), maxLeft);

        store.update({
            top,
            left,
            maxHeight,
            isPositioned: true,
        });
    }, [store]);

    const handleClose = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        store.update({
            isOpened: false,
            isPositioned: false,
        });
    }, [store]);

    const handleOpen = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        store.set('isOpened', true);
    };

    const handleMouseEnter = () => {
        if (!store.canHover) {
            return;
        }
        handleOpen();
    };

    const handleMouseLeave = () => {
        if (!store.canHover) {
            return;
        }
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }
        closeTimeout.current = setTimeout(handleClose, CLOSE_DELAY);
    };

    const handleClick = () => {
        if (store.canHover) {
            return;
        }
        if (store.isOpened) {
            handleClose();
            return;
        }
        handleOpen();
    };

    useEffect(() => {
        const mediaQueryList = window.matchMedia(HOVER_MEDIA_QUERY);
        const listener = (event: MediaQueryListEvent) => store.set('canHover', event.matches);

        mediaQueryList.addEventListener('change', listener);

        return () => mediaQueryList.removeEventListener('change', listener);
    }, [store]);

    useEffect(() => {
        return () => {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
        };
    }, []);

    useLayoutEffect(() => {
        if (!store.isOpened) {
            return;
        }

        updatePosition();

        const handleUpdatePosition = () => updatePosition();

        window.addEventListener('resize', handleUpdatePosition);
        window.addEventListener('scroll', handleUpdatePosition, true);

        const resizeObserver = new ResizeObserver(handleUpdatePosition);
        if (triggerRef.current) {
            resizeObserver.observe(triggerRef.current);
        }

        return () => {
            window.removeEventListener('resize', handleUpdatePosition);
            window.removeEventListener('scroll', handleUpdatePosition, true);
            resizeObserver.disconnect();
        };
    }, [store.isOpened, updatePosition]);

    useEffect(() => {
        if (!store.isOpened || !triggerRef.current) {
            return;
        }

        const intersectionObserver = new IntersectionObserver(
            entries => {
                if (entries.some(entry => !entry.isIntersecting)) {
                    handleClose();
                }
            },
            { threshold: 0 }
        );
        intersectionObserver.observe(triggerRef.current);

        return () => intersectionObserver.disconnect();
    }, [store.isOpened, handleClose]);

    useEffect(() => {
        if (!store.isOpened) {
            return;
        }

        const listener = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
                return;
            }
            handleClose();
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [store.isOpened, handleClose]);

    const items = useMemo(
        () => taskViews.slice().sort((a, b) => b.updatedAtMoment.valueOf() - a.updatedAtMoment.valueOf()),
        [taskViews]
    );

    return (
        <div ref={triggerRef} className={classnames('c-tt-task-views', className)}>
            <div
                className="c-tt-task-views__trigger"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <UiIcon.Eye size={20} />
                <span>{items.length}</span>
            </div>
            {store.isOpened && (
                <Portal>
                    <div
                        ref={dropdownRef}
                        className="c-tt-task-views__inner"
                        style={{
                            top: store.top,
                            left: store.left,
                            maxHeight: store.maxHeight,
                            visibility: store.isPositioned ? 'visible' : 'hidden',
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className="c-tt-task-views__label">Просмотры</div>
                        {items.length === 0 && (
                            <div className="c-tt-task-views__empty">Задачу ещё никто не просматривал</div>
                        )}
                        {items.map(taskView => (
                            <div key={taskView.id} className="c-tt-task-views__item">
                                <UiNavLink
                                    className="c-tt-task-views__item-user"
                                    to={ROUTES.USER(taskView.user.slug || taskView.user.id)}
                                >
                                    <UiAvatar
                                        size={24}
                                        label={taskView.user.previewName}
                                        image={taskView.user.imageAvatar}
                                    />
                                    <span className="c-tt-task-views__item-name">
                                        {taskView.user.name || taskView.user.previewName}
                                    </span>
                                </UiNavLink>
                                <div className="c-tt-task-views__item-date">
                                    {toDate(taskView.updatedAt).format('D MMMM, HH:mm')}
                                </div>
                            </div>
                        ))}
                    </div>
                </Portal>
            )}
        </div>
    );
});
